/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import ImportIcon from '@mui/icons-material/esm/CloudDownload';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { license } from '../../utils';
import { withUser } from '../../UserProvider';
import { buildErrorInfo, ErrorDialog } from './ErrorDialog';
import { ImportDialog } from './ImportDialog';

const convertLegacyFormat = (json) => ({
	machines: [json.data],
	streams: []
});

export const ImportDropzone = withUser(({ licenseInfo }) => ({ licenseInfo }))(
	({ children, enableClick = false, licenseInfo, scopeId, disabled, onDone = () => {}, onImporting = () => {} }) => {
		const [importData, setImportData] = useState(null);
		const [errorInfo, setErrorInfo] = useState(null);
		const setError = (title, message) => setErrorInfo(buildErrorInfo(title, message));

		const handleImport = (acceptedFiles) => {
			const canImport = license.areSheetsAvailable(licenseInfo);
			if (!canImport) {
				setError('Import.Error.NotPossible', 'License.StreamsheetsMaxReached');
			} else if (acceptedFiles.length === 1) {
				const fileReader = new FileReader();
				fileReader.onload = (event) => {
					const { result } = event.target;
					try {
						const parsedResult = JSON.parse(result);
						if (parsedResult.machines) {
							setImportData(parsedResult);
						} else if (parsedResult.data.machine) {
							// Import legacy format
							setImportData(convertLegacyFormat(parsedResult));
						} else {
							throw new Error('Invalid import file');
						}
					} catch (e) {
						setError('Import.Error.InvalidFile.Title', 'Import.Error.InvalidFile.Message');
					}
				};
				fileReader.readAsText(acceptedFiles[0]);
			} else if (acceptedFiles.length > 1) {
				setError('Import.Error.OnlyOneFile.Title', 'Import.Error.OnlyOneFile.Message');
			}
		};
		useEffect(() => {
			if (importData) {
				onImporting();
			}
		}, [importData]);

		return (
			<Dropzone
				disableClick={!enableClick}
				accept="application/json"
				onDropAccepted={handleImport}
				disabled={!!importData || disabled}
				style={{ width: '100%', height: '100%' }}
			>
				{({ isDragAccept }) => (
					<div style={{ width: '100%', height: '100%' }}>
						{isDragAccept ? (
							<div
								style={{
									position: 'absolute',
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: 'rgba(255, 255, 255, 0.3)',
									zIndex: 1
								}}
							>
								<ImportIcon
									style={{
										color: 'rgba(0, 0, 0, 0.7)',
										fontSize: '100pt'
									}}
								/>
							</div>
						) : null}
						{importData ? (
							<ImportDialog
								initialScopeId={scopeId}
								importData={importData}
								onClose={() => {
									setImportData(null);
									onDone();
								}}
							/>
						) : null}
						{errorInfo ? <ErrorDialog {...errorInfo} onClose={() => setErrorInfo(null)} /> : null}

						{children}
					</div>
				)}
			</Dropzone>
		);
	}
);
